import * as actionCreators from 'redux/eatd/actionCreators';
import {
  INSTANCE_EATD_TEST_OPERATOR_ADD,
  INSTANCE_EATD_TEST_OPERATOR_REMOVE,
  INSTANCE_EATD_ANONYMIZE_OPERATOR,
  INSTANCE_EATD_PREVIEW_OPERATOR,
  EATD_TEST_INCIDENT_REMOVE,
} from 'redux/eatd/constants';
import { apiAction } from 'redux/apiHelpers';

/**
 * @description Attaches the same meta object to each RSAA lifecycle action.
 * redux-api-middleware reads meta from each type descriptor, not from the
 * outer action object, so reducers can safely depend on action.meta.
 * @param {string[]} types
 * @param {Record<string, string>} meta
 * @returns {{type: string, meta: Record<string, string>}[]}
 */
const withMetaTypes = (types, meta) => {
  return types.map(type => ({ type, meta }));
};

const testerModeQuery = {
  testMode: true,
};

export const instanceEatdTestOperatorAdd = (id, topdeskOperatorId) => dispatch => {
  return dispatch(actionCreators.instanceEatdTestOperatorAdd(INSTANCE_EATD_TEST_OPERATOR_ADD, id, topdeskOperatorId));
};

export const instanceEatdTestOperatorRemove = topdeskOperatorId => dispatch => {
  return dispatch(actionCreators.instanceEatdTestOperatorRemove(INSTANCE_EATD_TEST_OPERATOR_REMOVE, topdeskOperatorId));
};

export const instanceEatdAnonymizeOperator = (id, topdeskOperatorId) => dispatch => {
  return dispatch(actionCreators.instanceEatdAnonymizeOperator(
    INSTANCE_EATD_ANONYMIZE_OPERATOR,
    id,
    topdeskOperatorId,
    testerModeQuery,
  ));
};

// --- Incident Test Actions --- 

/**
 * Action to add an incident ID to the test list in Redux state.
 * Use the lightweight test endpoint so Add does not depend on preview succeeding.
 */
export const instanceEatdTestIncidentAdd = (instanceId, incidentId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-incident/${instanceId}/test/incident/${incidentId}`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_TEST_INCIDENT_ADD}_REQUEST`,
      `${INSTANCE_EATD_TEST_INCIDENT_ADD}_SUCCESS`,
      `${INSTANCE_EATD_TEST_INCIDENT_ADD}_FAILURE`,
    ], { incidentId, instanceId }),
  });
};

/**
 * Action to remove an incident ID from the test list in Redux state.
 */
export const instanceEatdTestIncidentRemove = (incidentId) => {
  return {
    type: EATD_TEST_INCIDENT_REMOVE,
    payload: { incidentId },
  };
};

/**
 * Action to trigger applying EATD changes to a specific incident via the backend.
 */
export const instanceEatdApplyChangesToIncident = (instanceId, incidentId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-incident/${instanceId}/apply/${incidentId}?testMode=true`,
    method: 'POST',
    types: withMetaTypes([
      `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_REQUEST`,
      `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_SUCCESS`,
      `${INSTANCE_EATD_APPLY_CHANGES_INCIDENT}_FAILURE`,
    ], { incidentId }),
  });
};

/**
 * Preview EATD rule changes for a specific incident.
 * GET /api/instance/eatd-incident/:instanceId/preview/:incidentId
 */
export const instanceEatdPreviewChangesForIncident = (instanceId, incidentId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-incident/${instanceId}/preview/${incidentId}?testMode=true`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_REQUEST`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_SUCCESS`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT}_FAILURE`,
    ], { incidentId }),
  });
};

/**
 * Count incidents that currently match incident processing conditions.
 * This includes both profile eligibility and when/then rule matching.
 * GET /api/instance/eatd-incident/:instanceId/incidentsToProcess/count
 */
export const instanceEatdCountMatchingIncidents = (instanceId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-incident/${instanceId}/incidentsToProcess/count`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_REQUEST`,
      `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_SUCCESS`,
      `${INSTANCE_EATD_COUNT_MATCHING_INCIDENTS}_FAILURE`,
    ], { instanceId }),
  });
};

export const instanceEatdCountMatchingOperators = (instanceId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-operator/${instanceId}/operatorsToAnonymize/count`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_REQUEST`,
      `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_SUCCESS`,
      `${INSTANCE_EATD_COUNT_MATCHING_OPERATORS}_FAILURE`,
    ], { instanceId }),
  });
};

// --- BEGIN: Added Person Action Types ---
export const INSTANCE_EATD_TEST_PERSON_ADD = 'INSTANCE_EATD_TEST_PERSON_ADD';
export const INSTANCE_EATD_TEST_PERSON_REMOVE = 'INSTANCE_EATD_TEST_PERSON_REMOVE';
export const INSTANCE_EATD_APPLY_CHANGES_PERSON = 'INSTANCE_EATD_APPLY_CHANGES_PERSON';
export const INSTANCE_EATD_PREVIEW_CHANGES_PERSON = 'INSTANCE_EATD_PREVIEW_CHANGES_PERSON';
export const INSTANCE_EATD_COUNT_MATCHING_PERSONS = 'INSTANCE_EATD_COUNT_MATCHING_PERSONS';
// --- END: Added Person Action Types ---

export const INSTANCE_EATD_TEST_CHANGE_ADD = 'INSTANCE_EATD_TEST_CHANGE_ADD';
export const INSTANCE_EATD_TEST_CHANGE_REMOVE = 'INSTANCE_EATD_TEST_CHANGE_REMOVE';
export const INSTANCE_EATD_APPLY_CHANGES_CHANGE = 'INSTANCE_EATD_APPLY_CHANGES_CHANGE';
export const INSTANCE_EATD_PREVIEW_CHANGES_CHANGE = 'INSTANCE_EATD_PREVIEW_CHANGES_CHANGE';
export const INSTANCE_EATD_COUNT_MATCHING_CHANGES = 'INSTANCE_EATD_COUNT_MATCHING_CHANGES';

// --- BEGIN: Added Incident Action Types ---
export const INSTANCE_EATD_TEST_INCIDENT_ADD = 'INSTANCE_EATD_TEST_INCIDENT_ADD';
export const INSTANCE_EATD_TEST_INCIDENT_REMOVE = 'INSTANCE_EATD_TEST_INCIDENT_REMOVE';
export const INSTANCE_EATD_APPLY_CHANGES_INCIDENT = 'INSTANCE_EATD_APPLY_CHANGES_INCIDENT';
export const INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT = 'INSTANCE_EATD_PREVIEW_CHANGES_INCIDENT';
export const INSTANCE_EATD_COUNT_MATCHING_INCIDENTS = 'INSTANCE_EATD_COUNT_MATCHING_INCIDENTS';
export const INSTANCE_EATD_COUNT_MATCHING_OPERATORS = 'INSTANCE_EATD_COUNT_MATCHING_OPERATORS';
// --- END: Added Incident Action Types ---

/**
 * Preview anonymization for a specific operator
 * GET /api/instance/eatd-operator/:instanceId/preview/:operatorId
 */
export const instanceEatdTestPreviewAnonymization = (instanceId, topdeskOperatorId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-operator/${instanceId}/preview/operator/${topdeskOperatorId}?testMode=true`,
    method: 'GET',
    types: INSTANCE_EATD_PREVIEW_OPERATOR.map(type => ({type, meta: {topdeskOperatorId}})),
  });
};

/**
 * Add a person to the test list.
 * Use the lightweight test endpoint so Add only verifies the person exists.
 */
export const instanceEatdTestPersonAdd = (instanceId, personId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-person/${instanceId}/test/person/${personId}`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_TEST_PERSON_ADD}_REQUEST`,
      `${INSTANCE_EATD_TEST_PERSON_ADD}_SUCCESS`,
      `${INSTANCE_EATD_TEST_PERSON_ADD}_FAILURE`,
    ], { personId, instanceId }),
  });
};

/**
 * Remove a person from the test list (client-side state update).
 */
export const instanceEatdTestPersonRemove = (personId) => ({
  type: INSTANCE_EATD_TEST_PERSON_REMOVE,
  payload: { personId },
});

/**
 * Apply EATD rules to a specific person.
 * POST /api/instance/eatd-person/:instanceId/apply/:personId
 */
export const instanceEatdApplyChangesToPerson = (instanceId, personId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-person/${instanceId}/apply/${personId}?testMode=true`, // Use Person endpoint
    method: 'POST',
    types: withMetaTypes([
      `${INSTANCE_EATD_APPLY_CHANGES_PERSON}_REQUEST`,
      `${INSTANCE_EATD_APPLY_CHANGES_PERSON}_SUCCESS`,
      `${INSTANCE_EATD_APPLY_CHANGES_PERSON}_FAILURE`,
    ], { personId, instanceId }),
  });
};

/**
 * Preview EATD rule changes for a specific person.
 * GET /api/instance/eatd-person/:instanceId/preview/:personId
 */
export const instanceEatdPreviewChangesForPerson = (instanceId, personId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-person/${instanceId}/preview/${personId}?testMode=true`, // Use Person endpoint
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_REQUEST`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_SUCCESS`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_PERSON}_FAILURE`,
    ], { personId, instanceId }),
  });
};

export const instanceEatdCountMatchingPersons = (instanceId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-person/${instanceId}/personsToProcess/count`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_REQUEST`,
      `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_SUCCESS`,
      `${INSTANCE_EATD_COUNT_MATCHING_PERSONS}_FAILURE`,
    ], { instanceId }),
  });
};

export const instanceEatdTestChangeAdd = (instanceId, changeId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-change/${instanceId}/test/change/${changeId}`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_TEST_CHANGE_ADD}_REQUEST`,
      `${INSTANCE_EATD_TEST_CHANGE_ADD}_SUCCESS`,
      `${INSTANCE_EATD_TEST_CHANGE_ADD}_FAILURE`,
    ], { changeId, instanceId }),
  });
};

export const instanceEatdTestChangeRemove = changeId => ({
  type: INSTANCE_EATD_TEST_CHANGE_REMOVE,
  payload: { changeId },
});

export const instanceEatdApplyChangesToChange = (instanceId, changeId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-change/${instanceId}/apply/${changeId}?testMode=true`,
    method: 'POST',
    types: withMetaTypes([
      `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_REQUEST`,
      `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_SUCCESS`,
      `${INSTANCE_EATD_APPLY_CHANGES_CHANGE}_FAILURE`,
    ], { changeId, instanceId }),
  });
};

export const instanceEatdPreviewChangesForChange = (instanceId, changeId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-change/${instanceId}/preview/${changeId}?testMode=true`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_REQUEST`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_SUCCESS`,
      `${INSTANCE_EATD_PREVIEW_CHANGES_CHANGE}_FAILURE`,
    ], { changeId, instanceId }),
  });
};

export const instanceEatdCountMatchingChanges = (instanceId) => {
  return apiAction({
    endpoint: `/api/instance/eatd-change/${instanceId}/changesToProcess/count`,
    method: 'GET',
    types: withMetaTypes([
      `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_REQUEST`,
      `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_SUCCESS`,
      `${INSTANCE_EATD_COUNT_MATCHING_CHANGES}_FAILURE`,
    ], { instanceId }),
  });
};
